import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	NgbActiveModal,
	NgbProgressbarModule,
	NgbModal,
	NgbTooltip,
} from '@ng-bootstrap/ng-bootstrap';
import {
	trigger,
	transition,
	style,
	animate,
	query,
	stagger,
} from '@angular/animations';
import { PDFService } from '../pdf-service/pdf.service';
import { NotificationService } from '../notification-service/notification.service';
import { LoginService } from '../login-service/login.service';
import { BackendService } from '../backend-service/backend.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
	ImagePreviewModalComponent,
	ImagePreviewData,
} from '../image-preview-modal/image-preview-modal.component';

// Interface for storing original URL data for image extraction
interface StoredUrlData {
	originalHtml: string;
	sourceUrl: string;
	title?: string;
	extractedImageUrls?: string[];
}

// Interface for extracted image data
interface ExtractedImage {
	dataUrl: string;
	width: number;
	height: number;
	selected: boolean;
	pageNumber: number;
	imageIndex: number;
	sourceUrl?: string; // For URL-based images
	failedToLoad?: boolean; // Track loading failures
}

@Component({
	selector: 'app-pdf-image-extractor',
	standalone: true,
	imports: [CommonModule, FormsModule, NgbProgressbarModule, NgbTooltip],
	templateUrl: './pdf-image-extractor.component.html',
	styleUrls: ['./pdf-image-extractor.component.scss'],
	animations: [
		trigger('imageList', [
			transition('* => *', [
				query(
					'.image-item:enter',
					[
						style({
							opacity: 0,
							transform: 'scale(0.8) translateY(20px)',
						}),
						stagger('1200ms', [
							animate(
								'700ms cubic-bezier(0.35, 0, 0.25, 1)',
								style({
									opacity: 1,
									transform: 'scale(1) translateY(0)',
								}),
							),
						]),
					],
					{ optional: true },
				),
			]),
		]),
	],
})
export class PDFImageExtractorComponent implements OnInit, OnDestroy {
	// Services
	activeModal = inject(NgbActiveModal);
	pdfService = inject(PDFService);
	notificationService = inject(NotificationService);
	loginService = inject(LoginService);
	backendService = inject(BackendService);
	http = inject(HttpClient);
	cdr = inject(ChangeDetectorRef);
	private modalService = inject(NgbModal);

	// Component state
	pdfFile: File | null = null;
	urlData: any = null;
	extractionMode: 'pdf' | 'url' = 'pdf';
	extractedImages: ExtractedImage[] = [];
	isExtracting = false;
	extractionProgress = 0;
	errorMessage = '';
	hasExtracted = false;

	// Cancellation support
	private abortController: AbortController | null = null;

	// Selection state
	selectedImages: ExtractedImage[] = [];
	selectAll = false;

	ngOnInit(): void {
		// Component initialization
	}

	/**
	 * Component cleanup - releases memory used by extracted images
	 * This is critical for preventing memory leaks when dealing with large image data URLs
	 */
	ngOnDestroy(): void {
		// Cancel any ongoing extraction
		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}

		// Clear all extracted images to release memory
		// Data URLs can be very large, so this is important
		this.extractedImages = [];
		this.selectedImages = [];

		// Clear file references
		this.pdfFile = null;
		this.urlData = null;
	}

	/**
	 * Set the PDF file to extract images from
	 * @param file PDF file to process
	 */
	setPdfFile(file: File): void {
		this.pdfFile = file;
		this.extractionMode = 'pdf';
		this.startExtraction();
	}

	/**
	 * Set the URL data to extract images from
	 * @param urlData URL data containing image URLs
	 */
	setUrlData(urlData: StoredUrlData): void {
		this.urlData = urlData;
		this.extractionMode = 'url';
		this.startExtraction();
	}

	/**
	 * Start the image extraction process with progressive display
	 */
	private async startExtraction(): Promise<void> {
		if (this.extractionMode === 'pdf' && !this.pdfFile) {
			this.errorMessage = 'PDF-tiedostoa ei ole määritelty';
			return;
		}

		if (this.extractionMode === 'url' && !this.urlData) {
			this.errorMessage = 'URL-dataa ei ole määritelty';
			return;
		}

		this.isExtracting = true;
		this.extractionProgress = 10;
		this.errorMessage = '';
		this.extractedImages = []; // Clear existing images

		// Create new abort controller for this extraction
		this.abortController = new AbortController();

		try {
			if (this.extractionMode === 'pdf') {
				await this.extractFromPDF();
			} else if (this.extractionMode === 'url') {
				await this.extractFromURL();
			}

			this.extractionProgress = 100;
			this.hasExtracted = true;

			console.log(
				`Extraction completed. Final image count: ${this.extractedImages.length}`,
			);

			if (this.extractedImages.length === 0) {
				this.errorMessage =
					this.extractionMode === 'pdf'
						? 'PDF-tiedostosta ei löytynyt kuvia.'
						: 'Verkkosivulta ei löytynyt kuvia.';
			}
		} catch (error) {
			if (this.abortController?.signal.aborted) {
				console.log('Image extraction was cancelled by user');
				this.errorMessage =
					'Kuvien purku keskeytetty käyttäjän toimesta';
			} else {
				console.error('Error extracting images:', error);
				this.errorMessage =
					`Kuvien purkaminen ${this.extractionMode === 'pdf' ? 'PDF-tiedostosta' : 'verkkosivulta'} epäonnistui: ` +
					(error as Error).message;
			}
		} finally {
			this.isExtracting = false;
			this.abortController = null;
		}
	}

	/**
	 * Extract images from PDF file
	 */
	private async extractFromPDF(): Promise<void> {
		if (!this.pdfFile) return;

		// Extract raw images from PDF with progressive callbacks
		await this.pdfService.extractRawImagesFromPDF(
			this.pdfFile,
			// On image found callback - add to display immediately
			(image) => {
				const extractedImage: ExtractedImage = {
					dataUrl: image.dataUrl,
					width: image.width,
					height: image.height,
					selected: false,
					pageNumber: image.pageNumber,
					imageIndex: this.extractedImages.length,
				};
				this.extractedImages.push(extractedImage);

				// Sort by page number and then by image index
				this.extractedImages.sort((a, b) => {
					if (a.pageNumber !== b.pageNumber) {
						return a.pageNumber - b.pageNumber;
					}
					return a.imageIndex - b.imageIndex;
				});

				console.log(
					`Added image to display: Page ${image.pageNumber}, Total: ${this.extractedImages.length}`,
				);

				// Force Angular change detection to update UI immediately
				this.cdr.detectChanges();
			},
			// On progress callback - update progress bar
			(progress) => {
				// Calculate progress: 10% start + 80% for pages + 10% final
				const pageProgress =
					(progress.currentPage / progress.totalPages) * 80;
				this.extractionProgress = 10 + pageProgress;

				// Force change detection for progress updates too
				this.cdr.detectChanges();
			},
			// Pass abort signal for cancellation support
			this.abortController?.signal,
		);
	}

	/**
	 * Extract images from URL data
	 */
	private async extractFromURL(): Promise<void> {
		if (!this.urlData || !this.urlData.extractedImageUrls) return;

		const imageUrls = this.urlData.extractedImageUrls;
		const totalImages = imageUrls.length;

		for (let i = 0; i < imageUrls.length; i++) {
			if (this.abortController?.signal.aborted) {
				throw new Error('Cancelled by user');
			}

			const imageUrl = imageUrls[i];

			try {
				// Update progress
				const progressPercent = ((i + 1) / totalImages) * 80;
				this.extractionProgress = 10 + progressPercent;
				this.cdr.detectChanges();

				// Fetch image as blob
				const imageBlob = await this.fetchImageAsBlob(imageUrl);

				// Convert blob to data URL
				const dataUrl = await this.blobToDataUrl(imageBlob);

				// Create image to get dimensions
				const img = new Image();
				await new Promise<void>((resolve, reject) => {
					img.onload = () => resolve();
					img.onerror = () =>
						reject(
							new Error(
								'Failed to load image for dimension detection',
							),
						);
					img.src = dataUrl;
				});

				const extractedImage: ExtractedImage = {
					dataUrl: dataUrl,
					width: img.width,
					height: img.height,
					selected: false,
					pageNumber: 1, // URL images don't have pages
					imageIndex: this.extractedImages.length,
					sourceUrl: imageUrl,
					failedToLoad: false,
				};

				this.extractedImages.push(extractedImage);
				console.log(
					`Added URL image ${i + 1}/${totalImages}: ${imageUrl}`,
				);

				// Force change detection
				this.cdr.detectChanges();
			} catch (error) {
				console.warn(`Failed to load image ${imageUrl}:`, error);

				// Add failed image placeholder
				const failedImage: ExtractedImage = {
					dataUrl:
						'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjZjVmNWY1Ii8+CjxwYXRoIGQ9Ik01MCA3NUwyNSAzNUg3NUw1MCA3NVoiIGZpbGw9IiNkZGQiLz4KPHN2Zz4K',
					width: 100,
					height: 100,
					selected: false,
					pageNumber: 1,
					imageIndex: this.extractedImages.length,
					sourceUrl: imageUrl,
					failedToLoad: true,
				};

				this.extractedImages.push(failedImage);
				this.cdr.detectChanges();
			}
		}
	}

	/**
	 * Fetch image from URL as blob
	 */
	private async fetchImageAsBlob(imageUrl: string): Promise<Blob> {
		try {
			// Try to fetch directly first
			const response = await fetch(imageUrl, {
				mode: 'cors',
				signal: this.abortController?.signal,
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			return await response.blob();
		} catch (error) {
			// If direct fetch fails, try through backend service (to handle CORS)
			console.warn('Direct fetch failed, trying through backend:', error);

			try {
				// Use backend service to fetch image
				const backendResponse = await firstValueFrom(
					this.backendService.FetchImageFromURL(imageUrl),
				);
				if (backendResponse) {
					return backendResponse;
				}
				throw new Error('Backend service returned empty response');
			} catch (backendError) {
				console.error('Backend fetch also failed:', backendError);
				throw new Error(
					`Failed to fetch image: ${(error as Error).message}`,
				);
			}
		}
	}

	/**
	 * Convert blob to data URL
	 */
	private blobToDataUrl(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	/**
	 * Toggle selection of an individual image
	 * @param image Image to toggle selection for
	 */
	toggleImageSelection(image: ExtractedImage): void {
		image.selected = !image.selected;
		this.updateSelectedImages();
		this.updateSelectAllState();
	}

	/**
	 * Toggle selection of all images based on current checkbox state
	 * When checkbox is unchecked and clicked: select all available images
	 * When checkbox is checked and clicked: deselect all available images
	 */
	toggleSelectAll(): void {
		// Use the current checkbox state directly instead of toggling
		// If selectAll is currently false, user wants to select all
		// If selectAll is currently true, user wants to deselect all
		const shouldSelectAll = this.selectAll;

		this.extractedImages.forEach((image) => {
			image.selected = shouldSelectAll;
		});
		this.updateSelectedImages();
	}

	/**
	 * Update the selectedImages array based on current selections
	 */
	private updateSelectedImages(): void {
		this.selectedImages = this.extractedImages.filter(
			(image) => image.selected,
		);
	}

	/**
	 * Update the selectAll checkbox state based on individual selections
	 * Only update if all images are manually selected/deselected individually
	 */
	private updateSelectAllState(): void {
		const selectedCount = this.extractedImages.filter(
			(image) => image.selected,
		).length;

		// Only update selectAll state in clear cases to avoid conflicts with user intent
		if (selectedCount === 0) {
			// No images selected - checkbox should be unchecked
			this.selectAll = false;
		} else if (
			selectedCount === this.extractedImages.length &&
			this.extractedImages.length > 0
		) {
			// All images selected - checkbox should be checked
			this.selectAll = true;
		}
		// For partial selections, don't change selectAll state to avoid confusing the user
	}

	/**
	 * Get the count of selected images
	 * Counts directly from extractedImages to ensure real-time updates
	 */
	getSelectedCount(): number {
		return this.extractedImages.filter((image) => image.selected).length;
	}

	/**
	 * Close modal and return selected images as blobs
	 */
	async confirmSelection(): Promise<void> {
		if (this.selectedImages.length === 0) {
			this.notificationService.ShowToast(
				'Valitse vähintään yksi kuva jatkaaksesi',
				'warning',
			);
			return;
		}

		// Cancel any ongoing extraction before proceeding
		if (this.abortController && this.isExtracting) {
			console.log(
				'Cancelling ongoing extraction to proceed with selected images...',
			);
			this.abortController.abort();
		}

		try {
			// Convert selected images to Blob objects
			const imageBlobs: Blob[] = [];

			for (const image of this.selectedImages) {
				const blob = await this.dataUrlToBlob(image.dataUrl);
				imageBlobs.push(blob);
			}

			// Close modal with the selected image blobs
			this.activeModal.close({
				success: true,
				imageBlobs: imageBlobs,
				imageCount: this.selectedImages.length,
			});
		} catch (error) {
			console.error('Error converting images to blobs:', error);
			this.notificationService.ShowToast(
				'Virhe kuvien käsittelyssä: ' + (error as Error).message,
				'danger',
			);
		}
	}

	/**
	 * Convert data URL to Blob
	 * @param dataUrl Data URL string
	 * @returns Promise resolving to Blob
	 */
	private dataUrlToBlob(dataUrl: string): Promise<Blob> {
		return new Promise((resolve, reject) => {
			try {
				const byteString = atob(dataUrl.split(',')[1]);
				const mimeString = dataUrl
					.split(',')[0]
					.split(':')[1]
					.split(';')[0];

				const arrayBuffer = new ArrayBuffer(byteString.length);
				const uint8Array = new Uint8Array(arrayBuffer);

				for (let i = 0; i < byteString.length; i++) {
					uint8Array[i] = byteString.charCodeAt(i);
				}

				const blob = new Blob([arrayBuffer], { type: mimeString });
				resolve(blob);
			} catch (error) {
				reject(error);
			}
		});
	}

	/**
	 * Cancel and close modal without returning anything
	 */
	cancel(): void {
		// Cancel any ongoing extraction
		if (this.abortController) {
			console.log('Cancelling PDF extraction...');
			this.abortController.abort();
		}
		this.activeModal.dismiss();
	}

	/**
	 * Get display dimensions for thumbnail
	 * @param image Image to calculate dimensions for
	 */
	getThumbnailDimensions(image: ExtractedImage): string {
		return `${image.width} × ${image.height}`;
	}

	/**
	 * Get domain name from URL
	 * @param url URL to extract domain from
	 */
	getUrlDomain(url: string): string {
		try {
			const urlObj = new URL(url);
			return urlObj.hostname;
		} catch {
			return 'Unknown';
		}
	}

	/**
	 * Preview image in modal using the reusable ImagePreviewModalComponent
	 * @param image Image to preview
	 */
	previewImage(image: ExtractedImage): void {
		const modalRef = this.modalService.open(ImagePreviewModalComponent, {
			size: 'lg',
			centered: true,
			scrollable: true,
		});

		// Prepare image data for the modal
		const imageData: ImagePreviewData = {
			imageUrl: image.dataUrl,
			title:
				this.extractionMode === 'pdf'
					? `Kuva sivulta ${image.pageNumber}`
					: this.getUrlDomain(image.sourceUrl || ''),
			subtitle:
				this.extractionMode === 'pdf'
					? `${image.width} × ${image.height}`
					: image.sourceUrl,
			altText:
				this.extractionMode === 'pdf'
					? `Kuva sivulta ${image.pageNumber}`
					: 'Kuva verkkosivulta',
		};

		// Set the image data in the modal component
		modalRef.componentInstance.setImageData(imageData);
	}

	/**
	 * Track by function for ngFor to improve performance
	 * @param _ Index of the item (unused)
	 * @param image Image item
	 */
	trackByImageIndex(_: number, image: ExtractedImage): number {
		return image.imageIndex;
	}
}
